import {Feature} from 'ol'
import {Coordinate} from "ol/coordinate"
import {Extent} from "ol/extent";
import {WKT} from "ol/format"
import {LineString, MultiPoint, Point} from "ol/geom"
import {Fill, Stroke, Style, Circle as CircleStyle} from "ol/style";
import {TLoadTrack} from "@ui/api/track"
import {colors} from "@ui/common/colors"
import {averageLayer, routeLayer} from "@ui/services/common/layers";

export interface IRouteService {
  initTrack(): void
  appendPointToTrack(position: GeolocationPosition): void
  hideTrack(): void
  addRoute(route: TLoadTrack): void
  updateRouteProps(featureId: number, props: Partial<TLoadTrack["props"]>): void
  deleteRoute(featureId: number): void
  getTrackExtent(featureId: number): Extent | undefined

  drawAverage(point1: Coordinate, point2: Coordinate): void
}

class RouteService implements IRouteService {
  #track: Feature<LineString>
  #average: Feature<LineString>

  constructor() {
    this.#track = new Feature<LineString>({
      id: "track",
      geometry: new LineString([]),
    })

    this.#track.setStyle(new Style({
      zIndex: 10000,
      stroke: new Stroke({
        color: "#000000", 
        width: 2
      })
    }))

    this.#average = new Feature<LineString>({
      id: "average",
      geometry: new LineString([]),
    })

    this.#average.setStyle(new Style({
      zIndex: 10000,
      stroke: new Stroke({
        color: "#FF0000",
        width: 2
      })
    }))

    if (!averageLayer.getSource()?.hasFeature(this.#average)) {
      averageLayer.getSource()?.addFeature(this.#average)
    }
  }

  initTrack() {
    this.#track.setGeometry(new LineString([]))
    if (!routeLayer.getSource()?.hasFeature(this.#track)) {
      routeLayer.getSource()?.addFeature(this.#track)
    }
  }

  appendPointToTrack(position: GeolocationPosition){
		const {latitude, longitude} = position.coords
    this.#track.getGeometry()?.appendCoordinate([longitude, latitude])
  }

  hideTrack() {
    routeLayer.getSource()?.removeFeature(this.#track)
  }

  addRoute(route: TLoadTrack): void {
    const f = routeLayer.getSource()?.getFeatureById(route.props.id)
    if (f) return

    const geom = new WKT().readGeometry(route.geometry) as MultiPoint
    const feature = new Feature<LineString>({
      geometry: new LineString(geom.getCoordinates())
    })
    feature.setId(route.props.id)
    feature.setProperties(route.props)
    feature.setStyle((f) => {
      const color = colors[(f.getProperties()?.color || 1)]
      return [
        new Style({stroke: new Stroke({color: color[1], width: 8})}),
        new Style({stroke: new Stroke({color: color[0], width: 3})}),
        new Style({
          geometry: new Point((f as Feature<LineString>).getGeometry()?.getFirstCoordinate()!),
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({color: '#FFFFFF'}),
            stroke: new Stroke({color: color[1], width: 2}),
          }),
        }),
        new Style({
          geometry: new Point((f as Feature<LineString>).getGeometry()?.getLastCoordinate()!),
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({color: '#FFFFFF'}),
            stroke: new Stroke({color: color[1], width: 2}),
          }),
        })
      ]
    })
    routeLayer.getSource()?.addFeature(feature)
  }

  updateRouteProps(featureId: number, props: Partial<TLoadTrack["props"]>) {
    const feature = routeLayer.getSource()?.getFeatureById(featureId)
    feature && feature.setProperties(props)
  }

  deleteRoute(featureId: number): void {
    const feature = routeLayer.getSource()?.getFeatureById(featureId)
    feature && routeLayer.getSource()?.removeFeature(feature)
  }

  getTrackExtent(featureId: number): Extent | undefined {
		const feature = routeLayer.getSource()?.getFeatureById(featureId)
    return feature?.getGeometry()?.getExtent()
	}

  drawAverage(point1: Coordinate, point2: Coordinate): void {
    this.#average.setGeometry(new LineString([point1, point2]))
  }

}

export default new RouteService() as IRouteService
